/**
 * NiceScale utility — computes "nice" axis tick values for charts.
 *
 * Uses the standard 1‑2‑5 nicestep algorithm:
 *   step = k × 10ⁿ   where  k ∈ {1, 2, 5}
 *
 * The utility is intentionally generic so it can be reused across any chart
 * that needs automatically‑computed, human‑readable axis tick positions.
 */

/**
 * Rounds a rough step value up to the nearest "nice" step.
 * Nice steps are of the form k × 10ⁿ where k ∈ {1, 2, 5}.
 *
 * @param {number} roughStep
 * @returns {number}
 */
function niceStep(roughStep) {
  if (roughStep <= 0) return 1;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;

  let k;
  if (normalized <= 1) k = 1;
  else if (normalized <= 2) k = 2;
  else if (normalized <= 5) k = 5;
  else k = 10;

  return k * magnitude;
}

/**
 * Computes a nice axis scale for a set of numeric data values.
 *
 * @param {number[]} values       - Data values for this axis (nulls/NaN filtered internally).
 * @param {number}   [targetTicks=6] - Desired approximate number of tick marks.
 * @param {Object}   [opts]
 * @param {number}   [opts.minStep=0]  - Smallest allowed step. Pass 1 for an axis whose
 *   labels are rounded to integers, so two ticks never format to the same label.
 * @param {boolean}  [opts.nonNegative=false] - When every value is >= 0 and they are all
 *   identical, start the axis at 0 instead of padding below it (no "-1 km" ticks), and
 *   keep about targetTicks ticks.
 *   Both options default to the previous behaviour, so existing callers are unchanged.
 * @returns {{
 *   min:       number,   // Nice lower bound (≤ data min)
 *   max:       number,   // Nice upper bound (≥ data max)
 *   step:      number,   // Interval between ticks
 *   ticks:     number[], // Tick values from min to max inclusive
 *   tickCount: number    // Number of intervals  (= ticks.length − 1)
 * }}
 */
export function computeNiceAxis(values, targetTicks = 6, { minStep = 0, nonNegative = false } = {}) {
  const filtered = (values || []).filter(
    (v) => typeof v === "number" && isFinite(v)
  );

  if (filtered.length === 0) {
    return {
      min: 0,
      max: 100,
      step: 20,
      ticks: [0, 20, 40, 60, 80, 100],
      tickCount: 5,
    };
  }

  const dataMin = Math.min(...filtered);
  const dataMax = Math.max(...filtered);

  // Edge case: all values identical
  if (dataMin === dataMax) {
    const pad = Math.abs(dataMin) * 0.1 || 1;
    const step = Math.max(niceStep(pad), minStep);
    let min = Math.floor((dataMin - pad) / step) * step;
    let max = Math.ceil((dataMax + pad) / step) * step;
    if (nonNegative && dataMin >= 0 && min < 0) {
      // e.g. every point at x = 0: 0..5 with step 1, not -1..1
      min = 0;
      max = Math.max(max, step * (targetTicks - 1));
    }
    const tickCount = Math.round((max - min) / step);
    const ticks = Array.from(
      { length: tickCount + 1 },
      (_, i) => +((min + i * step).toFixed(10))
    );
    return { min, max, step, ticks, tickCount };
  }

  const range = dataMax - dataMin;
  const rough = range / (targetTicks - 1);
  const step = Math.max(niceStep(rough), minStep);

  const min = Math.floor(dataMin / step) * step;
  const max = Math.ceil(dataMax / step) * step;
  const tickCount = Math.round((max - min) / step);
  const ticks = Array.from(
    { length: tickCount + 1 },
    (_, i) => +((min + i * step).toFixed(10))
  );

  return { min, max, step, ticks, tickCount };
}

/**
 * Convenience wrapper: computes nice scales for both axes of a scatter chart.
 *
 * @param {Object[]} data         - Array of data objects.
 * @param {string}   xKey         - Property name for X values.
 * @param {string}   yKey         - Property name for Y values.
 * @param {Object}   [opts]
 * @param {number}   [opts.xTargetTicks=6] - Target tick count for the X axis.
 * @param {number}   [opts.yTargetTicks=6] - Target tick count for the Y axis.
 * @returns {{ x: AxisConfig, y: AxisConfig }}
 */
export function computeScatterAxisConfig(
  data,
  xKey,
  yKey,
  { xTargetTicks = 6, yTargetTicks = 6 } = {}
) {
  const xValues = (data || [])
    .map((d) => d[xKey])
    .filter((v) => typeof v === "number" && isFinite(v));
  const yValues = (data || [])
    .map((d) => d[yKey])
    .filter((v) => typeof v === "number" && isFinite(v));

  return {
    x: computeNiceAxis(xValues, xTargetTicks),
    y: computeNiceAxis(yValues, yTargetTicks),
  };
}
